class ExternalScriptError extends Error {
  constructor(src: string) {
    super(`Failed to load external script: ${src}`);
    this.name = 'ExternalScriptError';
  }
}

interface ScriptOptions {
  scriptId?: string;
  insertBeforeAllTags?: boolean;
  nonce?: string;
  async?: boolean;
  defer?: boolean;
  maxRetries?: number;
  crossOrigin?: string;
  integrity?: string;
}

export function loadAppleSsoScript({
  scriptId = '',
  insertBeforeAllTags = false,
  nonce = '',
  async = true,
  defer = true,
}: ScriptOptions = {}): Promise<void> {
  let src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

  // Check if already loaded
  if ((window as any).AppleID) {
    console.log('Apple SSO: SDK already loaded, skipping script load');
    return Promise.resolve();
  }

  console.log('Apple SSO: Loading Apple Sign-In SDK from:', src);

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = scriptId;
    script.async = async;
    script.defer = defer;
    if (nonce) script.nonce = nonce;

    script.onload = () => {
      console.log('Apple SSO: Script loaded successfully');
      // Wait a bit for the SDK to initialize
      setTimeout(() => {
        if ((window as any).AppleID) {
          console.log('Apple SSO: SDK available after script load');
          resolve();
        } else {
          console.error('Apple SSO: SDK not available after script load timeout');
          reject(new Error('Apple Sign-In SDK not available after script load'));
        }
      }, 1000);
    };

    script.onerror = (event: any) => {
      const target = event.target as HTMLScriptElement;
      console.error('Apple SSO: Script load failed:', target.src);
      reject(new ExternalScriptError(target.src));
    };

    script.src = src;

    if (insertBeforeAllTags) {
      const firstScriptTagInDocument = document.getElementsByTagName('script')[0];
      firstScriptTagInDocument.parentNode?.insertBefore(script, firstScriptTagInDocument);
    } else {
      document.body.appendChild(script);
    }
  });
}

export function loadFacebookSdkScript({
  scriptId = 'facebook-jssdk',
  insertBeforeAllTags = false,
  nonce = '',
  async = true,
  defer = true,
}: ScriptOptions = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if ((window as any).FB) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.async = async;
    script.defer = defer;
    if (nonce) script.nonce = nonce;

    script.onload = () => resolve();
    script.onerror = (event: any) => {
      const target = event.target as HTMLScriptElement;
      reject(new ExternalScriptError(target.src));
    };
    script.src = 'https://connect.facebook.net/en_US/sdk.js';

    if (insertBeforeAllTags) {
      const firstScriptTagInDocument = document.getElementsByTagName('script')[0];
      firstScriptTagInDocument.parentNode?.insertBefore(script, firstScriptTagInDocument);
    } else {
      document.body.appendChild(script);
    }
  });
}

export function loadMicrosoftMsalScript({
  scriptId = 'msal-script',
  insertBeforeAllTags = false,
  nonce = '',
  async = true,
  defer = true,
}: ScriptOptions = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if ((window as any).msal) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.async = async;
    script.defer = defer;
    if (nonce) script.nonce = nonce;

    script.onload = () => resolve();
    script.onerror = (event: any) => {
      const target = event.target as HTMLScriptElement;
      reject(new ExternalScriptError(target.src));
    };
    script.src = 'https://alcdn.msauth.net/browser/2.30.0/js/msal-browser.min.js';

    if (insertBeforeAllTags) {
      const firstScriptTagInDocument = document.getElementsByTagName('script')[0];
      firstScriptTagInDocument.parentNode?.insertBefore(script, firstScriptTagInDocument);
    } else {
      document.body.appendChild(script);
    }
  });
}
